import { Edit, Refresh, Star, StarBorder } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { orderBy } from "lodash";
import { useSnackbar } from "notistack";
import { ReactElement, ReactNode, useState } from "react";
import { useMemo } from "react";

import {
  EditOwnedPhoneNumberInput,
  MutationArchiveOwnedPhoneNumberArgs,
  MutationEditOwnedPhoneNumberArgs,
  MutationUnarchiveOwnedPhoneNumberArgs,
  OwnedPhoneNumberProvisioningState,
  PhoneNumberMessageClass,
  PhoneNumberState,
  PhoneNumberSubaccountState,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import Table, { RowAction, TableColumns } from "~frontend/components/Table";
import Archive from "~frontend/components/icons/Archive";
import Unarchive from "~frontend/components/icons/Unarchive";
import {
  ARCHIVE_OWNED_PHONE_NUMBER,
  EDIT_OWNED_PHONE_NUMBER,
  REFRESH_OWNED_PHONE_NUMBER_STATUS,
  UNARCHIVE_OWNED_PHONE_NUMBER,
} from "~frontend/graphql/ownedPhoneNumber";
import { GET_SUBACCOUNT_ADMIN_FIELDS } from "~frontend/graphql/subaccounts";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import { SubaccountAdminData } from "~frontend/providers/LoggedInUserProvider/context";
import { useNonCacheClearingApolloClient } from "~frontend/providers/NonCacheClearingApolloClientProvider";
import {
  useSubaccount,
  useSubaccountAdminConfig,
} from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import { prettyPhoneNumber } from "~frontend/util/phoneNumber";
import useMutation from "~frontend/util/useMutation";

import { PHONE_NUMBER_REFETCH_QUERIES } from ".";
import EditOwnedPhoneNumberDialog from "./EditOwnedPhoneNumberDialog";
import { PhoneNumberSortBy } from "./constants";
import { OwnedPhoneNumberForList } from "./types";

type OwnedPhoneNumberWithAggregateState = OwnedPhoneNumberForList & {
  isArchived: boolean;
  isActive: boolean; // Means: Can the number be used in text campaigns?
  isProvisioning: boolean;
  isFailed: boolean;
  sortState: number;
};

const RowWrapper = ({
  row,
  children,
}: {
  row: OwnedPhoneNumberWithAggregateState;
  children: ReactNode;
}) => {
  return !(row.isActive || row.isProvisioning) || row.isArchived ? (
    <Box sx={{ color: palette.darkGray, fontStyle: "italic" }}>{children}</Box>
  ) : (
    children
  );
};

export const NumberStatusTag = ({
  children,
  backgroundColor,
  color,
}: {
  children: ReactNode;
  backgroundColor: string;
  color: string;
}): ReactElement => {
  return (
    <Typography
      component={"span"}
      sx={{
        px: 0.5,
        fontWeight: 600,
        fontSize: "10px",
        fontStyle: "normal",
        backgroundColor,
        color,
      }}
    >
      {children}
    </Typography>
  );
};

export function PhoneNumberTable({
  rows,
  providerFilter,
  sort,
  setDefaultPhoneNumber,
  showCampaignId = false,
}: {
  rows: OwnedPhoneNumberForList[];
  providerFilter: string[] | null;
  sort: string | null;
  setDefaultPhoneNumber?: (
    phoneNumber: OwnedPhoneNumberForList
  ) => Promise<void>;
  showCampaignId?: boolean;
}): ReactElement {
  const { subaccount } = useSubaccount()!;
  const { defaultMessagingStrategy } = useSubaccountAdminConfig()!;
  const isStaff = useCurrentUserIsStaff();
  const { enqueueSnackbar } = useSnackbar();
  const subaccountApolloClient = useNonCacheClearingApolloClient();

  const columns: TableColumns<OwnedPhoneNumberWithAggregateState> = [
    {
      title: "Number",
      key: "phoneNumber",
      renderValue: row => {
        const stateTag = () => {
          if (phoneNumberIsDefault(defaultMessagingStrategy, row)) {
            return (
              <NumberStatusTag
                backgroundColor={palette.lightOrange}
                color={palette.navy}
              >
                Default
              </NumberStatusTag>
            );
          } else if (row.isArchived) {
            return (
              <NumberStatusTag
                backgroundColor={palette.lightGray}
                color={palette.darkGray}
              >
                Archived
              </NumberStatusTag>
            );
          } else if (row.provisioningState === "PARTIAL" && isStaff) {
            return (
              <NumberStatusTag
                backgroundColor={palette.darkBlue}
                color={palette.white}
              >
                Partial
              </NumberStatusTag>
            );
          } else if (row.isProvisioning || row.state === "NEW") {
            return (
              <NumberStatusTag
                backgroundColor={palette.lightGray}
                color={palette.darkGray}
              >
                Pending
              </NumberStatusTag>
            );
          } else if (row.provisioningState === "FAILED") {
            if (!isStaff) {
              console.error(
                "A failed phone number is being displayed to a non-staff user"
              );
              return <span />;
            }
            return (
              <NumberStatusTag
                backgroundColor={palette.red}
                color={palette.white}
              >
                Failed
              </NumberStatusTag>
            );
          } else {
            return "";
          }
        };

        return (
          <RowWrapper row={row}>
            <FlexBox gap={1} alignItems="center">
              <span>{row.phoneNumber}</span>
              {stateTag()}
            </FlexBox>
          </RowWrapper>
        );
      },
    },
    {
      title: "Name",
      key: "displayName",
      align: "left",
      renderValue: row => {
        const formattedName = row.displayName
          ? row.displayName
          : prettyPhoneNumber(row.phoneNumber);

        return <RowWrapper row={row}>{formattedName}</RowWrapper>;
      },
    },
    ...(showCampaignId && isStaff
      ? ([
          {
            title: "Campaign ID",
            key: "tenDlcCampaignId",
            renderValue: row => (
              <RowWrapper row={row}>{row.tenDlcCampaignId || "--"}</RowWrapper>
            ),
            align: "right",
          },
        ] as TableColumns<OwnedPhoneNumberWithAggregateState>)
      : []),
    {
      title: "Created At",
      key: "createdAt",
      align: "left",
      renderValue: row => (
        <RowWrapper row={row}>
          {new Date(row.createdAt).toLocaleString()}
        </RowWrapper>
      ),
    },
  ];

  const filteredPhoneNumbers = useMemo(() => {
    return providerFilter
      ? rows.filter(phoneNumber =>
          providerFilter.includes(phoneNumber.provider)
        )
      : rows;
  }, [rows, providerFilter]);

  const aggregateStatePhoneNumbers: OwnedPhoneNumberWithAggregateState[] =
    filteredPhoneNumbers.map(p => {
      const opnSubaccount = p.subaccounts.find(
        s => s.subaccountId === subaccount.id
      );
      if (!opnSubaccount) {
        console.error("Subaccount not found for phone number", p);
      }

      const isActive =
        p.state === PhoneNumberState.Active &&
        opnSubaccount?.state === PhoneNumberSubaccountState.Active &&
        (p.provisioningState === OwnedPhoneNumberProvisioningState.Complete ||
          p.provisioningState === OwnedPhoneNumberProvisioningState.Partial);

      const isProvisioning =
        p.state === PhoneNumberState.New &&
        (p.provisioningState ===
          OwnedPhoneNumberProvisioningState.Provisioning ||
          p.provisioningState === OwnedPhoneNumberProvisioningState.New);

      const isArchived =
        p.state === PhoneNumberState.InitialsNotAllowed ||
        opnSubaccount?.state === PhoneNumberSubaccountState.InitialsNotAllowed;

      const isFailed =
        p.provisioningState === OwnedPhoneNumberProvisioningState.Failed;

      const sortState = () => {
        if (isProvisioning || isActive) {
          return 0;
        } else if (isArchived) {
          return 1;
        } else if (isFailed) {
          return 2;
        } else {
          console.error("Unexpected phone number state", p);
          return 0;
        }
      };

      return {
        ...p,
        sortState: sortState(),
        isArchived,
        isActive,
        isProvisioning,
        isFailed,
      };
    });

  const sortedPhoneNumbers = useMemo(() => {
    switch (sort) {
      case PhoneNumberSortBy.CreatedAtAsc:
        return orderBy(
          aggregateStatePhoneNumbers,
          ["sortState", "createdAt"],
          ["asc", "asc"]
        );
      case PhoneNumberSortBy.CreatedAtDesc:
        return orderBy(
          aggregateStatePhoneNumbers,
          ["sortState", "createdAt"],
          ["asc", "desc"]
        );
      case PhoneNumberSortBy.NumberAsc:
        return orderBy(
          aggregateStatePhoneNumbers,
          ["sortState", "phoneNumber"],
          ["asc", "asc"]
        );
      case PhoneNumberSortBy.NumberDesc:
        return orderBy(
          aggregateStatePhoneNumbers,
          ["sortState", "phoneNumber"],
          ["asc", "desc"]
        );
      default: {
        return orderBy(aggregateStatePhoneNumbers, "sortState", "asc");
      }
    }
  }, [aggregateStatePhoneNumbers, sort]);

  const [ownedPhoneNumberToEdit, setOwnedPhoneNumberToEdit] = useState(
    null as OwnedPhoneNumberWithAggregateState | null
  );

  const [editOwnedPhoneNumber] = useMutation<MutationEditOwnedPhoneNumberArgs>(
    EDIT_OWNED_PHONE_NUMBER,
    {
      refetchQueries: PHONE_NUMBER_REFETCH_QUERIES,
    }
  );

  const handleOwnedPhoneNumberEdit = async (
    value: EditOwnedPhoneNumberInput
  ): Promise<void> => {
    const { errors } = await editOwnedPhoneNumber({
      variables: {
        input: {
          subaccountId: subaccount!.id,
          ...value,
          tcrCampaignId: isStaff ? value.tcrCampaignId : undefined,
        },
      },
    });

    if (errors) {
      throw errors;
    } else {
      await subaccountApolloClient.refetchQueries({
        include: [GET_SUBACCOUNT_ADMIN_FIELDS],
      });
    }
    setOwnedPhoneNumberToEdit(null);
  };

  const [archiveOwnedPhoneNumber] =
    useMutation<MutationArchiveOwnedPhoneNumberArgs>(
      ARCHIVE_OWNED_PHONE_NUMBER,
      {
        refetchQueries: PHONE_NUMBER_REFETCH_QUERIES,
      }
    );
  const handleArchiveNumber = async (
    value: OwnedPhoneNumberWithAggregateState
  ) => {
    const { errors } = await archiveOwnedPhoneNumber({
      variables: {
        input: { ownedPhoneNumberId: value.id, subaccountId: subaccount.id },
      },
    });

    if (errors) {
      enqueueSnackbar("Failed to archive phone number", { variant: "error" });
    } else {
      await subaccountApolloClient.refetchQueries({
        include: [GET_SUBACCOUNT_ADMIN_FIELDS],
      });
    }
  };

  const [unarchiveOwnedPhoneNumber] =
    useMutation<MutationUnarchiveOwnedPhoneNumberArgs>(
      UNARCHIVE_OWNED_PHONE_NUMBER,
      {
        refetchQueries: PHONE_NUMBER_REFETCH_QUERIES,
      }
    );
  const handleUnarchiveNumber = async (
    value: OwnedPhoneNumberWithAggregateState
  ) => {
    const { errors } = await unarchiveOwnedPhoneNumber({
      variables: {
        input: { ownedPhoneNumberId: value.id, subaccountId: subaccount.id },
      },
    });

    if (errors) {
      enqueueSnackbar("Failed to restore phone number", { variant: "error" });
    } else {
      await subaccountApolloClient.refetchQueries({
        include: [GET_SUBACCOUNT_ADMIN_FIELDS],
      });
    }
  };

  const [refreshPhoneNumber] =
    useMutation<MutationUnarchiveOwnedPhoneNumberArgs>(
      REFRESH_OWNED_PHONE_NUMBER_STATUS,
      { refetchQueries: PHONE_NUMBER_REFETCH_QUERIES }
    );
  const handleRefreshNumber = async (
    value: OwnedPhoneNumberWithAggregateState
  ) => {
    const { errors } = await refreshPhoneNumber({
      variables: {
        input: { ownedPhoneNumberId: value.id, subaccountId: subaccount.id },
      },
    });

    if (errors) {
      enqueueSnackbar("Failed to refresh phone number", { variant: "error" });
    } else {
      await subaccountApolloClient.refetchQueries({
        include: [GET_SUBACCOUNT_ADMIN_FIELDS],
      });
    }
  };

  const archiveAction = (
    ownedPhoneNumber: OwnedPhoneNumberWithAggregateState
  ) => {
    if (
      ownedPhoneNumber.messageClass !== PhoneNumberMessageClass.ShortCode &&
      !ownedPhoneNumber.isArchived
    ) {
      return {
        id: "archive-owned-phone-number",
        type: "button",
        confirm: (ownedPhoneNumber: OwnedPhoneNumberWithAggregateState) => ({
          title: "Archive Phone Number",
          description: `${ownedPhoneNumber.displayName || prettyPhoneNumber(ownedPhoneNumber.phoneNumber)} will no longer be available to use in new texting campaigns after archiving.`,
          cancellationText: "Cancel",
          confirmationText: "Archive",
        }),
        icon: <Archive />,
        tooltipLabel: "Archive Phone Number",
        onClick: (ownedPhoneNumber: OwnedPhoneNumberWithAggregateState) =>
          handleArchiveNumber(ownedPhoneNumber),
      };
    }
  };

  const editAction = (ownedPhoneNumber: OwnedPhoneNumberWithAggregateState) => {
    if (
      ownedPhoneNumber.isActive ||
      ownedPhoneNumber.isProvisioning ||
      (isStaff && ownedPhoneNumber.state === PhoneNumberState.New)
    ) {
      return {
        id: "edit-owned-phone-number",
        type: "button",
        icon: <Edit />,
        tooltipLabel: "Edit Phone Number",
        onClick: (ownedPhoneNumber: OwnedPhoneNumberWithAggregateState) =>
          setOwnedPhoneNumberToEdit(ownedPhoneNumber),
      };
    }
  };

  const makeDefaultAction = (
    ownedPhoneNumber: OwnedPhoneNumberWithAggregateState
  ) => {
    if (ownedPhoneNumber.isActive && setDefaultPhoneNumber) {
      return {
        id: "make-phone-number-default",
        type: "button",
        confirm: {
          title: "Set Phone Number as Default",
          description: `Make ${ownedPhoneNumber.displayName || prettyPhoneNumber(ownedPhoneNumber.phoneNumber)} the default phone number?`,
          cancellationText: "Cancel",
          confirmationText: "Confirm",
        },
        icon: phoneNumberIsDefault(
          defaultMessagingStrategy,
          ownedPhoneNumber
        ) ? (
          <Star />
        ) : (
          <StarBorder />
        ),
        tooltipLabel: "Set as Default",
        onClick: () => setDefaultPhoneNumber(ownedPhoneNumber),
      };
    }
  };

  const refreshOpnAction = (
    ownedPhoneNumber: OwnedPhoneNumberWithAggregateState
  ) => {
    if (
      isStaff &&
      ownedPhoneNumber.messageClass === PhoneNumberMessageClass.TenDlc &&
      (ownedPhoneNumber.isProvisioning || ownedPhoneNumber.isFailed) &&
      !ownedPhoneNumber.isArchived
    ) {
      return {
        id: "refresh-owned-phone-number",
        type: "button",
        confirm: (ownedPhoneNumber: OwnedPhoneNumberWithAggregateState) => ({
          title: "Refresh Phone Number",
          description: `${ownedPhoneNumber.displayName || prettyPhoneNumber(ownedPhoneNumber.phoneNumber)} will be checked for status updates.`,
          cancellationText: "Cancel",
          confirmationText: "Refresh",
        }),
        icon: <Refresh />,
        tooltipLabel: "Refresh Phone Number",
        onClick: (ownedPhoneNumber: OwnedPhoneNumberWithAggregateState) =>
          handleRefreshNumber(ownedPhoneNumber),
      };
    }
  };

  const unarchiveOpnAction = (
    ownedPhoneNumber: OwnedPhoneNumberWithAggregateState
  ) => {
    if (
      isStaff &&
      ownedPhoneNumber.messageClass !== PhoneNumberMessageClass.ShortCode &&
      ownedPhoneNumber.isArchived
    ) {
      return {
        id: "unarchive-owned-phone-number",
        type: "button",
        confirm: (ownedPhoneNumber: OwnedPhoneNumberWithAggregateState) => ({
          title: "Restore Phone Number",
          description: `${ownedPhoneNumber.displayName || prettyPhoneNumber(ownedPhoneNumber.phoneNumber)} will be available to use in new texting campaigns after restoring.`,
          cancellationText: "Cancel",
          confirmationText: "Restore",
        }),
        icon: <Unarchive />,
        tooltipLabel: "Restore Phone Number",
        onClick: (ownedPhoneNumber: OwnedPhoneNumberWithAggregateState) =>
          handleUnarchiveNumber(ownedPhoneNumber),
      };
    }
  };

  const rowActions = (ownedPhoneNumber: OwnedPhoneNumberWithAggregateState) => {
    return [
      archiveAction(ownedPhoneNumber),
      editAction(ownedPhoneNumber),
      makeDefaultAction(ownedPhoneNumber),
      refreshOpnAction(ownedPhoneNumber),
      unarchiveOpnAction(ownedPhoneNumber),
    ].filter(Boolean) as RowAction<OwnedPhoneNumberWithAggregateState>[];
  };

  return (
    <>
      <EditOwnedPhoneNumberDialog
        open={!!ownedPhoneNumberToEdit}
        ownedPhoneNumber={ownedPhoneNumberToEdit}
        onClose={() => setOwnedPhoneNumberToEdit(null)}
        onSubmit={handleOwnedPhoneNumberEdit}
      />
      <Table
        columns={columns}
        rows={sortedPhoneNumbers}
        noFooter={true}
        alignRightAfterIdx={1}
        emptyState="No phone numbers"
        rowActions={(ownedPhoneNumber: OwnedPhoneNumberWithAggregateState) =>
          rowActions(ownedPhoneNumber)
        }
      />
    </>
  );
}

function phoneNumberIsDefault(
  defaultMessagingStrategy: SubaccountAdminData["defaultMessagingStrategy"],
  phoneNumber: OwnedPhoneNumberForList
): boolean {
  if (!defaultMessagingStrategy) {
    return false;
  }

  switch (defaultMessagingStrategy.__typename) {
    case "CampaignMessagingStrategyBandwidth10Dlc":
    case "CampaignMessagingStrategyBandwidthShortCode":
    case "CampaignMessagingStrategyBandwidthTollFree":
    case "CampaignMessagingStrategyTwilioShortCode":
    case "CampaignMessagingStrategySinch10Dlc":
    case "CampaignMessagingStrategyFakeShortCode":
    case "CampaignMessagingStrategyFake10Dlc":
    case "CampaignMessagingStrategyFakeTollFree":
      return (
        phoneNumber.id === defaultMessagingStrategy.config.ownedPhoneNumber.id
      );
    case "CampaignMessagingStrategyFake":
      return (
        phoneNumber.provider === "FAKE" && !phoneNumber.ownedPhoneNumberPool
      );
    case "CampaignMessagingStrategyBandwidth10DlcPool":
    case "CampaignMessagingStrategyBandwidthUnregisteredPool":
    case "CampaignMessagingStrategyFakePool":
      return false;
  }
}
